/**
 * AI Mode Types
 * Types for the guided AI design generation workflow
 */

import type { AISubCategory } from './navigation.types';
import type { ProductCategory } from './product.types';

/**
 * A selectable option with image preview (Style, Mood)
 */
export interface AIOption {
  id: string;
  label: string;
  imageUrl?: string;
}

/**
 * Slot categories mapping to existing AIState fields
 */
export type SlotCategory = 'style' | 'mood' | 'text' | 'elements' | 'noun';

/**
 * A single fillable blank in a Mad Libs display sentence
 */
export interface MadLibsSlot {
  readonly id: string;
  readonly category: SlotCategory;
  readonly placeholder: string;
  readonly required: boolean;
  /** Pre-filled value for noun blanks (sentence reads naturally before user edits) */
  readonly defaultValue?: string;
  /** Selectable options shown as chips (for noun blanks) */
  readonly options?: readonly string[];
}

/**
 * Display sentence template with slot markers
 * sentence uses ${slotId} syntax for fillable blanks
 */
export interface DisplaySentence {
  readonly sentence: string;
  readonly slots: readonly MadLibsSlot[];
}

/**
 * Prompt configuration for a template
 * Only UI-facing fields remain — prompt generation uses displaySentences.ts
 */
export interface TemplatePromptConfig {
  /** Optional: Suggested elements specific to this template (shown as quick-select chips) */
  suggestedElements?: string[];
  /** Optional: Placeholder text for the text input (gives users context) */
  textPlaceholder?: string;
}

/**
 * Layout variant for a template
 * Each variant defines its own prompt, strength, defaults, and preview image
 */
export interface LayoutVariant {
  id: string;
  label: string;
  description: string;
  imageUrl: string;
  /** Product-specific preview images. Key = product ID. Falls back to imageUrl. */
  productImages?: Record<string, string>;
  strength: number;
  /** Style option ID from STYLE_OPTIONS (e.g., 'cross-hatching') */
  defaultStyle: string;
  /** Mood option ID from MOOD_OPTIONS (e.g., 'playful') */
  defaultMood: string;
  suggestedElements: string[];
  textPlaceholder: string;
  /** Hint only - does not block generation */
  requiresImage: boolean;
  /** Optional Mad Libs display sentence for this layout */
  displaySentence?: DisplaySentence;
  /**
   * Optional custom base image URL — overrides template-level customBaseImageUrl
   * Used when layouts within the same template need different base images (e.g., QR centered vs bottom)
   */
  customBaseImageUrl?: string;
  /**
   * Preset text for custom prompt templates
   * When this layout is selected, this text pre-populates the custom prompt text box
   */
  customPromptPreset?: string;
  /**
   * Product IDs to exclude this layout from (e.g., ['black-wallet'])
   * Layout will not appear for these products even if the parent template is available
   */
  excludeProducts?: readonly string[];
}

/**
 * Template configuration for AI design generation
 * Use-case driven templates (Pet Dog Tag, Military Memorial, etc.)
 * Replaces the abstract LayoutConfig with marketing-backed templates
 */
export interface TemplateConfig {
  /** Unique identifier */
  id: string;
  /** Display label (e.g., "Pet Dog Tag", "Military Memorial") */
  label: string;
  /** Marketing description shown in UI */
  description?: string;
  /** Preview image URL for template selection UI */
  imageUrl?: string;
  /** Product-specific preview images. Key = product ID. Falls back to imageUrl. */
  productImages?: Record<string, string>;
  /** AI generation strength (0.75-0.85) - lower preserves more, higher allows more creativity */
  strength: number;
  /**
   * Product ID restrictions - if empty/undefined, available for all products
   * Uses product IDs ('black-dog-tag', 'black-card', 'black-wallet')
   */
  allowedProducts?: string[];
  /**
   * Alternative: restrict by category instead of specific products
   * Uses ProductCategory ('tag' | 'card' | 'keychain' | 'wallet')
   */
  allowedCategories?: ProductCategory[];
  /**
   * Optional custom base image URL for Fal.ai
   * If provided, overrides product's default exportFrame
   * Use for specialized template images (e.g., QR code placeholder frame)
   */
  customBaseImageUrl?: string;
  /**
   * If true, after AI generation redirect to Manual mode with QR upload prompt
   * Used for templates with blank areas where user needs to upload their own QR code
   */
  requiresQRUpload?: boolean;
  /**
   * Max characters for user-authored custom prompt
   * When set, template shows a text box for custom prompt input
   * The prompt template should include {customPrompt} placeholder
   */
  customPromptMaxLength?: number;
  /** Optional Mad Libs display sentence (fallback when layout has none) */
  displaySentence?: DisplaySentence;
  /** Optional layout variants for this template */
  layouts?: LayoutVariant[];
  /** UI-facing prompt configuration (suggested elements, text placeholder) */
  prompt: TemplatePromptConfig;
}

/**
 * @deprecated Use TemplateConfig instead
 * Kept temporarily for backwards compatibility during migration
 */
export type LayoutConfig = TemplateConfig;

/**
 * Toggle states for optional AI features
 */
export interface ToggleState {
  upload: boolean;
  text: boolean;
  elements: boolean;
}

/**
 * Current state of the AI design flow
 */
export interface AIState {
  style: AIOption | null;
  template: TemplateConfig | null;
  selectedLayout: LayoutVariant | null;
  mood: AIOption | null;
  elements: string[]; // Max 3 elements
  nouns: Record<string, string>; // Keyed by slot ID, e.g. {'noun-1': 'doves'}
  text: string; // Max 75 characters
  uploadedImage: string | null; // Base64 or blob URL for Fal.ai
  currentStep: AISubCategory;
  toggles: ToggleState; // Toggle states for optional features
  isGenerating: boolean;
  generatedImageUrl: string | null;
  compositeImageUrl: string | null; // Generated image with white frame overlay
  error: string | null;
  isSaving: boolean;
  saveError: string | null;
}

/**
 * Validation result for generate button
 */
export interface AIValidationResult {
  isValid: boolean;
  missingFields: ('style' | 'template' | 'mood')[];
}

/**
 * Actions for AI state reducer
 */
export type AIAction =
  | { type: 'SET_STYLE'; payload: AIOption | null }
  | { type: 'SET_TEMPLATE'; payload: TemplateConfig }
  | { type: 'SET_LAYOUT'; payload: LayoutVariant | null }
  | { type: 'SET_MOOD'; payload: AIOption | null }
  | { type: 'ADD_ELEMENT'; payload: string }
  | { type: 'REMOVE_ELEMENT'; payload: number }
  | { type: 'SET_NOUN'; payload: { slotId: string; value: string } }
  | { type: 'SET_TEXT'; payload: string }
  | { type: 'SET_UPLOADED_IMAGE'; payload: string | null }
  | { type: 'SET_STEP'; payload: AISubCategory }
  | { type: 'SET_TOGGLE'; payload: { toggle: keyof ToggleState; value: boolean } }
  | { type: 'START_GENERATING' }
  | { type: 'GENERATION_SUCCESS'; payload: string }
  | { type: 'GENERATION_ERROR'; payload: string }
  | { type: 'COMPOSITE_READY'; payload: string }
  | { type: 'START_SAVING' }
  | { type: 'SAVE_SUCCESS' }
  | { type: 'SAVE_ERROR'; payload: string }
  | { type: 'RESET' };

/**
 * Context value for AI mode
 */
export interface AIContextValue {
  state: AIState;
  dispatch: React.Dispatch<AIAction>;
  setStyle: (style: AIOption | null) => void;
  setTemplate: (template: TemplateConfig) => void;
  setLayout: (layout: LayoutVariant | null) => void;
  setMood: (mood: AIOption | null) => void;
  addElement: (element: string) => void;
  removeElement: (index: number) => void;
  setNoun: (slotId: string, value: string) => void;
  setText: (text: string) => void;
  setUploadedImage: (image: string | null) => void;
  setToggle: (toggle: keyof ToggleState, value: boolean) => void;
  goToStep: (step: AISubCategory) => void;
  goToNextStep: () => void;
  validate: () => AIValidationResult;
  generate: (
    productImageUrl: string,
    imageSize?: { width: number; height: number },
    displaySize?: { width: number; height: number }
  ) => Promise<void>;
  saveToLibrary: (compositeDataUrl: string, productId: string) => Promise<boolean>;
  reset: () => void;
}

/**
 * Constants
 */
export const MAX_ELEMENTS = 3;
export const MAX_TEXT_LENGTH = 75;
export const MAX_CUSTOM_PROMPT_LENGTH = 200;
export const MAX_NOUN_LENGTH = 16;

/**
 * Step order for simplified 2-step workflow
 * Template panel is the hub with inline style/mood/toggles
 */
export const AI_STEP_ORDER: AISubCategory[] = ['template', 'builder', 'preview'];
